import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import * as yup from 'yup';

import FormControls from 'components/FormControls/FormControls';
import InlineForm from 'components/InlineForm/InlineForm';
import Panel from 'components/Panel/Panel';
import RadioButton from 'components/RadioButton/RadioButton';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { AssessmentSettings } from 'hooks/useGetAssessmentSettings';
import useNotification from 'hooks/useNotification';
import useUpdateAssessmentSettings from 'hooks/useUpdateAssessmentSettings';
import { t } from 'locales/i18n';
import { AssessmentDetailsContext } from 'providers/AssessmentDetailsProvider';

interface GeneralProps {
  interviewUid: string;
  data?: AssessmentSettings;
  isLoading: boolean;
}

enum FormFields {
  VISIBILITY = 'visibility',
}

interface FormData {
  [FormFields.VISIBILITY]: boolean;
}

const General: React.FC<GeneralProps> = ({ interviewUid, data, isLoading }) => {
  const { isPractice } = useContext(AssessmentDetailsContext);

  const {
    updateAssessmentSettings,
    isLoading: isLoadingUpdateAssessmentSettings,
  } = useUpdateAssessmentSettings();

  const { showErrorNotification, showSuccessNotification } = useNotification();
  const queryClient = useQueryClient();

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.VISIBILITY]: yup
          .boolean()
          .required(t('Assessment visibility is required')),
      }),
    [FormFields.VISIBILITY]
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    values: {
      [FormFields.VISIBILITY]: data?.is_public ?? false,
    },
  });

  const onSubmit: SubmitHandler<FormData> = async ({ visibility }) => {
    await updateAssessmentSettings(
      {
        interviewUid,
        isPublic: visibility,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Assessment settings saved'),
            t('Your settings were successfully saved')
          );
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentSettings', interviewUid],
          });
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while updating assessment'),
            error.message
          );
        },
      }
    );
  };

  const invitationMethods = [
    { id: 1, name: t('Invite-only'), value: false },
    { id: 2, name: t('Public'), value: true },
  ];

  return isLoading ? (
    <Skeleton height={250} />
  ) : (
    <Panel title={t('General')}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={false} className="flex flex-col gap-6">
          {!isPractice && (
            <InlineForm
              title={t('Visibility')}
              description={t(
                'Select whether applicants need an invitation to access the assessment.'
              )}
            >
              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0 lg:pl-12">
                <Controller
                  control={control}
                  name={FormFields.VISIBILITY}
                  render={({ field }) => (
                    <>
                      {invitationMethods.map(({ id, name, value }, index) => (
                        <RadioButton
                          name={name}
                          id={id.toString()}
                          key={id.toString()}
                          checked={watch(FormFields.VISIBILITY) === value}
                          onChange={() => field.onChange(value)}
                          index={index}
                          dataTestId={name.toLocaleLowerCase()}
                        />
                      ))}
                    </>
                  )}
                />
              </div>
            </InlineForm>
          )}
          <InlineForm
            title={t('Practice')}
            description={t('Set if this assessment is a practice.')}
          >
            <div className="lg:pl-12">
              <ToggleSwitch
                enabled={data?.is_practice ?? false}
                setEnabled={() => false}
                classNames="cursor-not-allowed opacity-30"
                disabled
              />
            </div>
          </InlineForm>

          {isDirty && (
            <FormControls
              isLoading={isLoadingUpdateAssessmentSettings}
              onCancel={() => reset()}
            />
          )}
        </fieldset>
      </form>
    </Panel>
  );
};

export default General;
